import './switch.css';

import type { EventKey } from 'cadenza/utils/event-util';
import { ChangeEvent, forwardEnterToClick, on, unByKey } from 'cadenza/utils/event-util';
import { uniqueId } from 'cadenza/utils/unique-id';

export interface SwitchOptions {
  checked?: boolean;
  disabled?: boolean;
}

export class Switch extends HTMLElement {

  #labelEventKey?: EventKey;

  /**
   * @param [options] - Options
   * @param [options.isChecked=false] - Whether the switch is initially checked
   */
  constructor (options: SwitchOptions = {}) {
    super();
    this.applyOptions(options);

    this.addEventListener('keydown', forwardEnterToClick);
    this.addEventListener('click', () => {
      if (!this.disabled) {
        this.checked = !this.checked;
        this.dispatchChange();
      }
    });
  }

  connectedCallback () {
    this.classList.add('d-switch', 'form-group-control');
    this.setAttribute('role', 'switch');

    /* eslint-disable no-self-assign -- setup the state attributes */
    this.disabled = this.disabled;
    this.checked = this.checked;

    const labelledById = this.getAttribute('aria-labelledby');
    const label = labelledById
      ? document.getElementById(labelledById)
      : this.closest('label');
    if (label) {
      if (!labelledById) {
        label.id ||= uniqueId();
        this.setAttribute('aria-labelledby', label.id);
      }
      this.#labelEventKey = on(label, 'click', (event) => {
        // We don't want to handle a click which
        // bubbled up from a switch nested in its label.
        if (event.target !== this) {
          this.click();
        }
      });
    }
  }

  disconnectedCallback () {
    unByKey(this.#labelEventKey);
  }

  set disabled (value) {
    if (value) {
      this.setAttribute('aria-disabled', 'true');
    } else {
      this.removeAttribute('aria-disabled');
    }
    this.tabIndex = value ? -1 : 0;
  }

  get disabled () {
    return this.getAttribute('aria-disabled') === 'true';
  }

  set checked (value) {
    this.setAttribute('aria-checked', Boolean(value).toString());
  }

  get checked () {
    return this.getAttribute('aria-checked') === 'true';
  }

  applyOptions (options: SwitchOptions) {
    const { checked, disabled } = options;
    if (checked !== undefined) {
      this.checked = checked;
    }
    if (disabled !== undefined) {
      this.disabled = disabled;
    }
  }

  dispatchChange () {
    this.dispatchEvent(new ChangeEvent('change', { detail: { checked: this.checked } }));
  }

}

customElements.define('d-switch', Switch);
